import type { JsonApiResource } from './project';

// Project Reference Category - matches backend enum.
// Legacy "primary aspect" mirror post-restructure (see `aspects` below): kept so
// older flows that read `category` keep working. `aspects` is the source of truth.
export type ProjectReferenceCategory =
  | 'color'
  | 'material'
  | 'style'
  | 'output'
  | 'data';

// Project Reference Aspect - multi-select tag set for inspiration images.
// A reference can be style AND material AND color at once (unlike the
// mutually-exclusive `category`). Canonical order: style > material > color.
export type ProjectReferenceAspect = 'style' | 'material' | 'color';

export const PROJECT_REFERENCE_ASPECTS: ProjectReferenceAspect[] = [
  'style',
  'material',
  'color',
];

// Classification Status - matches backend enum
export type ClassificationStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed';

// Reference Type - user uploaded or system provided
export type ReferenceType = 'user' | 'system';

// Reference Section - which intake bucket the reference belongs to.
// `brief` = the building's current state ("what I have": existing photos,
// drawings, measurements, docs); `reference` = the desired look ("what I like":
// inspiration, material/colour samples). Defaults to `reference` server-side.
export type ProjectReferenceSection = 'brief' | 'reference';

// File info structure
export interface ProjectReferenceFile {
  url: string;
  width?: number;
  height?: number;
  size?: number;
  mime_type?: string;
}

// Classification metadata for images
export interface ImageClassificationMetadata {
  confidence?: number;
  description?: string;
  tags?: string[];
  colors_detected?: string[];
  material_types?: string[];
  architectural_style?: string | null;
  usage_recommendations?: string;
}

// Extraction metadata for documents
export interface DocumentExtractionMetadata {
  document_title?: string;
  relevance_score?: number;
  usage_recommendations?: string;
}

// Moderation Status - content moderation result
export type ModerationStatus = 'rejected' | null;

// Project Reference Attributes (matches API response)
export interface ProjectReferenceAttributes {
  reference_type: ReferenceType;
  section: ProjectReferenceSection;
  is_system: boolean;
  // Legacy single-category mirror (= primary aspect). Prefer `aspects`.
  category: ProjectReferenceCategory | null;
  category_label: string | null;
  // Multi-select aspect set (style/material/color) for inspiration images,
  // canonical order; null for output-style and document references.
  aspects: ProjectReferenceAspect[] | null;
  description: string | null;
  classification_status: ClassificationStatus;
  moderation_status: ModerationStatus;
  is_classified: boolean;
  can_delete: boolean;
  original_filename: string | null;
  file_url: string;
  metadata?: {
    original_filename?: string;
    file_size?: number;
    content_type?: string;
    classification?: ImageClassificationMetadata;
    extraction?: DocumentExtractionMetadata;
  };
  created_at: string;
  updated_at: string;
}

// Project Reference Resource
export interface ProjectReference extends JsonApiResource<ProjectReferenceAttributes> {
  type: 'project_reference';
}

// Upload Policy from API (presigned PUT for Cloudflare R2).
// When `skip_upload: true` the file is already in R2 (md5 dedup);
// the client must skip the S3 PUT and go straight to confirm — in this
// branch `url` is empty and `expires_in` is absent.
export interface UploadPolicy {
  method: 'PUT';
  url: string;
  headers: Record<string, string>;
  expires_in?: number;
  skip_upload?: boolean;
}

// Store Response with upload info
export interface ProjectReferenceStoreResponse {
  type: 'project_reference';
  id: string;
  attributes: ProjectReferenceAttributes & {
    upload_policy: UploadPolicy;
  };
}

// Category display info
export const CATEGORY_LABELS: Record<ProjectReferenceCategory, string> = {
  color: 'Color Palette',
  material: 'Material Sample',
  style: 'Style Reference',
  output: 'Output Style',
  data: 'Document Data',
};

export const CATEGORY_COLORS: Record<ProjectReferenceCategory, string> = {
  color: 'bg-pink-100 text-black dark:bg-pink-900/30 dark:text-white',
  material: 'bg-amber-100 text-black dark:bg-amber-900/30 dark:text-white',
  style: 'bg-blue-100 text-black dark:bg-blue-900/30 dark:text-white',
  output: 'bg-purple-100 text-black dark:bg-purple-900/30 dark:text-white',
  data: 'bg-gray-100 text-black dark:bg-gray-900/30 dark:text-white',
};

// Classification status display
export const STATUS_LABELS: Record<ClassificationStatus, string> = {
  pending: 'Pending',
  processing: 'Processing...',
  completed: 'Classified',
  failed: 'Failed',
};

// Helper to check if reference is an image
// Also checks pending refs by content_type since category is null until classified
export function isImageReference(reference: ProjectReference): boolean {
  const category = reference.attributes.category;

  // If classified, check category
  if (category) {
    return (
      category === 'color' ||
      category === 'material' ||
      category === 'style' ||
      category === 'output'
    );
  }

  // For pending/processing refs, check content_type
  const contentType = reference.attributes.metadata?.content_type;
  if (contentType) {
    return contentType.startsWith('image/');
  }

  // Default to image if unknown (most uploads are images)
  return (
    reference.attributes.classification_status === 'pending' ||
    reference.attributes.classification_status === 'processing'
  );
}

// Helper to check if reference is a document
// Also checks pending refs by content_type since category is null until classified
export function isDocumentReference(reference: ProjectReference): boolean {
  const category = reference.attributes.category;

  // If classified, check category
  if (category) {
    return category === 'data';
  }

  // For pending/processing refs, check content_type
  const contentType = reference.attributes.metadata?.content_type;
  if (contentType) {
    return (
      contentType === 'application/pdf' ||
      contentType === 'text/plain' ||
      contentType === 'text/markdown'
    );
  }

  return false;
}

// True when the reference's underlying FILE is an image we can preview, REGARDLESS
// of its classified category. A brief document (category 'data') is often an image
// (a satellite/cadastral map, a site plan) — we still want to show the actual
// picture, not a generic file icon. Keyed off the file mime / filename extension,
// not the category (category drives placement/type, this drives rendering).
export function hasImageFile(reference: ProjectReference): boolean {
  if (!reference.attributes.file_url) return false;
  const contentType = reference.attributes.metadata?.content_type;
  if (contentType) return contentType.startsWith('image/');
  // Fallback when the mime is missing: infer from the original filename.
  const name =
    reference.attributes.original_filename ??
    reference.attributes.metadata?.original_filename ??
    '';
  return /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(name);
}

// Helper to check if classification is complete
export function isClassified(reference: ProjectReference): boolean {
  return reference.attributes.is_classified;
}

// Helper to check if reference is a system (global) reference
export function isSystemReference(reference: ProjectReference): boolean {
  return reference.attributes.is_system;
}

// Helper to check if reference was rejected by content moderation
export function isRejectedReference(reference: ProjectReference): boolean {
  return reference.attributes.moderation_status === 'rejected';
}

// Effective aspect set for a reference, with the gap-#3 legacy fallback.
// `aspects` is the source of truth, but when it resolves empty (an old API
// response that only sent `category`, or a future server that sends only
// aspects this client cannot map) we fall back to the legacy `category` mirror
// so the reference never vanishes from the per-aspect "All" buckets.
export function effectiveAspects(
  reference: ProjectReference,
): ProjectReferenceAspect[] {
  const raw = reference.attributes.aspects;
  const known = (raw ?? []).filter((a): a is ProjectReferenceAspect =>
    PROJECT_REFERENCE_ASPECTS.includes(a),
  );

  if (known.length > 0) return known;

  const category = reference.attributes.category;
  if (
    category === 'style' ||
    category === 'material' ||
    category === 'color'
  ) {
    return [category];
  }

  return [];
}

// Whether a reference carries the given aspect (source of truth = `aspects`,
// legacy `category` as fallback).
export function hasAspect(
  reference: ProjectReference,
  aspect: ProjectReferenceAspect,
): boolean {
  return effectiveAspects(reference).includes(aspect);
}
